import axios from 'axios';
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileSignature, Users, Clock, Save, Eye, AlertTriangle, CheckCircle, History, Edit3, Loader2, FileText, Upload, Download } from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { TiptapContractEditor } from "@/components/crm/TiptapContractEditor";
import { SectionSignaturesGlobal } from "@/pages/gestion-contrats/SectionSignaturesGlobal";
import { UploadContractDialog } from "@/pages/gestion-contrats/UploadContractDialog";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPES_CONTRAT = [
  { key: "saisonnier_peintre", label: "Saisonnier — Peintre", badge: "bg-amber-100 text-amber-800", defaultTitre: "Contrat de travail saisonnier — Peintre" },
  { key: "permanent_peintre", label: "Permanent — Peintre", badge: "bg-blue-100 text-blue-800", defaultTitre: "Contrat de travail permanent — Peintre" },
  { key: "permanent_chef_equipe", label: "Permanent — Chef d'équipe", badge: "bg-purple-100 text-purple-800", defaultTitre: "Contrat de travail permanent — Chef d'équipe" },
  { key: "permanent_charge_projet", label: "Permanent — Chargée de projet", badge: "bg-pink-100 text-pink-800", defaultTitre: "Contrat de travail permanent — Chargée de projet" },
  { key: "permanent_adjoint", label: "Permanent — Adjoint(e) admin.", badge: "bg-slate-100 text-slate-800", defaultTitre: "Contrat de travail permanent — Adjoint(e) administratif(ve)" },
  { key: "sous_traitant_lmds", label: "Sous-traitant — Installateur LMDS", badge: "bg-emerald-100 text-emerald-800", defaultTitre: "Contrat de sous-traitance — Installateur Le Marché du Store" },
];

export const GestionContrats = () => {
  const [templates, setTemplates] = useState({});  // { type_key: template }
  const [activeType, setActiveType] = useState("saisonnier_peintre");
  const [editContent, setEditContent] = useState("");
  const [editTitre, setEditTitre] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [contractHistory, setContractHistory] = useState([]);
  
  // 🆕 Modal configuration contrat personnalisé
  const [showConfigContract, setShowConfigContract] = useState(false);
  const [configEmployee, setConfigEmployee] = useState(null);
  const [customVars, setCustomVars] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);

  // 🆕 [Phase 102 — 2026-07-09] Aperçu PDF avec données bidon (Julie Tremblay)
  const [previewingPdf, setPreviewingPdf] = useState(false);

  // 🚀 [Phase v13.56 — 2026-07-22] Upload .docx dialog
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tplRes, pendRes] = await Promise.all([
        axios.get(`${API_URL}/api/contracts/template`),
        axios.get(`${API_URL}/api/contracts/pending-signatures`)
      ]);
      const tplData = tplRes.data;
      const pendData = pendRes.data;

      // Construire le dictionnaire par type
      const tplMap = {};
      for (const tpl of (tplData.templates || [])) {
        tplMap[tpl.type_template] = tpl;
      }
      setTemplates(tplMap);

      // Initialiser l'éditeur avec le type actif
      const current = tplMap[activeType];
      if (current) {
        setEditContent(current.content || "");
        setEditTitre(current.titre || "");
      } else {
        const typeDef = TYPES_CONTRAT.find(t => t.key === activeType);
        setEditContent("");
        setEditTitre(typeDef?.defaultTitre || "");
      }

      setPending(pendData.pending || []);
    } catch (e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  const switchType = (typeKey) => {
    setActiveType(typeKey);
    const tpl = templates[typeKey];
    const typeDef = TYPES_CONTRAT.find(t => t.key === typeKey);
    setEditContent(tpl?.content || "");
    setEditTitre(tpl?.titre || typeDef?.defaultTitre || "");
  };

  const saveTemplate = async () => {
    if (!editContent.trim()) { toast.error("Le contenu ne peut pas être vide"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/contracts/template`, { content: editContent, titre: editTitre, type_template: activeType });
      if (res.status === 200) {
        toast.success("✅ Contrat sauvegardé");
        loadData();
      } else { toast.error("Erreur sauvegarde"); }
    } catch (e) { toast.error("Erreur sauvegarde"); }
    finally { setSaving(false); }
  };

  const viewHistory = async (employee) => {
    setHistoryEmployee(employee);
    try {
      const res = await axios.get(`${API_URL}/api/contracts/history/${employee.employee_id}`);
      const data = res.data;
      setContractHistory(data.signatures || []);
    } catch (e) { setContractHistory([]); }
    setShowHistory(true);
  };

  const openConfigContract = (employee) => {
    setConfigEmployee(employee);
    // Initialiser les variables personnalisées avec des valeurs par défaut
    setCustomVars({
      salaire_horaire: "",
      date_embauche: new Date().toISOString().split('T')[0],
    });
    setShowConfigContract(true);
  };

  const saveContractConfig = async () => {
    if (!configEmployee) return;
    
    // Validation
    if (!customVars.salaire_horaire || parseFloat(customVars.salaire_horaire) <= 0) {
      toast.error("Le salaire horaire doit être supérieur à 0");
      return;
    }
    
    setSavingConfig(true);
    try {
      // Sauvegarder les variables personnalisées dans la base de données
      await axios.put(`${API_URL}/api/employees/${configEmployee.employee_id}/contract-variables`, {
        custom_variables: customVars
      });
      
      toast.success(`✅ Contrat configuré pour ${configEmployee.employee_nom}`);
      setShowConfigContract(false);
      loadData(); // Recharger pour voir le statut mis à jour
    } catch (e) {
      toast.error("Erreur lors de la configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  // 🆕 [Phase 102 — 2026-07-09] Génération de l'aperçu PDF avec données bidon
  const handlePreviewPdf = async () => {
    if (!editContent.trim()) {
      toast.error("Le contenu ne peut pas être vide");
      return;
    }
    setPreviewingPdf(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/contracts/template/preview-pdf`,
        {
          content: editContent,
          titre: editTitre || "Aperçu du contrat",
          type_template: activeType,
        },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Erreur lors de la génération de l'aperçu PDF");
    } finally {
      setPreviewingPdf(false);
    }
  };

  // 🚀 [v13.56] Handler post-upload : recharge templates + switch sur le nouveau
  const handleUploaded = async (result) => {
    await loadData();
    toast.success(`Nouveau template actif : ${result.template?.titre}`);
  };

  // 🚀 [v13.56] Retélécharger .docx original
  const downloadOriginalDocx = async () => {
    const tpl = templates[activeType];
    if (!tpl?.original_docx_gridfs_id) {
      toast.error("Aucun fichier .docx original pour ce template");
      return;
    }
    try {
      const res = await axios.get(
        `${API_URL}/api/contracts/templates/${tpl.id}/original`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = tpl.original_docx_filename || `contrat_${tpl.id.slice(0,8)}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Téléchargement lancé");
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestion des Contrats</h1>
          <p className="text-slate-500 text-sm mt-1">Rédigez et gérez les contrats de travail par type de poste</p>
        </div>
        <div className="flex items-center gap-2">
          {pending.length > 0 && (
            <Badge variant="destructive" className="text-sm px-3 py-1">
              <AlertTriangle className="w-4 h-4 mr-1" />
              {pending.length} en attente
            </Badge>
          )}
          <Button
            onClick={() => setShowUpload(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="btn-upload-docx"
          >
            <Upload className="w-4 h-4 mr-2" /> Uploader un contrat .docx
          </Button>
        </div>
      </div>

      {/* Sélecteur de type de contrat */}
      <div className="flex flex-wrap gap-2">
        {TYPES_CONTRAT.map(t => {
          const hasTpl = !!templates[t.key];
          return (
            <button
              key={t.key}
              onClick={() => switchType(t.key)}
              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                activeType === t.key
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'
              }`}
            >
              {t.label}
              {hasTpl && <span className="ml-2 w-2 h-2 rounded-full bg-emerald-400 inline-block" title="Contrat configuré" />}
            </button>
          );
        })}
      </div>

      <Tabs defaultValue="redaction">
        <TabsList>
          <TabsTrigger value="redaction"><Edit3 className="w-4 h-4 mr-2" />Rédaction</TabsTrigger>
          <TabsTrigger value="signatures">
            <Users className="w-4 h-4 mr-2" />
            Signatures en attente
            {pending.length > 0 && <Badge variant="destructive" className="ml-2 text-xs">{pending.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ====== RÉDACTION ====== */}
        <TabsContent value="redaction" className="mt-4 space-y-4">
          {templates[activeType] && (
            <div className="flex items-center justify-between gap-2 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Version {templates[activeType].version} — Dernière modification: {new Date(templates[activeType].updated_at).toLocaleDateString('fr-CA')}</span>
                {templates[activeType].source_type === "upload_docx" && (
                  <Badge className="bg-purple-100 text-purple-800 text-xs ml-2">Uploadé (.docx)</Badge>
                )}
              </div>
              {templates[activeType].original_docx_gridfs_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadOriginalDocx}
                  data-testid="btn-download-original-docx"
                  className="gap-1.5"
                  title="Fichier .docx original tel que fourni par la firme d'avocats"
                >
                  <Download className="w-3.5 h-3.5" /> Fichier avocat original
                </Button>
              )}
            </div>
          )}
          {!templates[activeType] && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
              <AlertTriangle className="w-4 h-4" />
              Aucun contrat configuré pour ce type. Rédigez-en un ci-dessous et cliquez Sauvegarder.
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Contenu du contrat</CardTitle>
              <CardDescription>
                Rédigez votre contrat en texte riche. Cliquez les <strong>chips ci-dessous</strong> pour insérer une variable dynamique (nom de l&apos;employé, salaire horaire, etc.) qui sera remplie automatiquement lors de la signature.
                <br />
                <strong>Permanents</strong> : re-signature requise chaque 1<sup>er</sup> janvier.{" "}
                <strong>Saisonniers</strong> : re-signature requise chaque 15 avril.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titre du contrat</Label>
                <Input
                  value={editTitre}
                  onChange={e => setEditTitre(e.target.value)}
                  placeholder="ex: Contrat de travail saisonnier 2026"
                  className="mt-1"
                  data-testid="contrat-titre-input"
                />
              </div>
              <div>
                <Label>Contenu du contrat</Label>
                <div className="mt-1">
                  <TiptapContractEditor
                    value={editContent}
                    onChange={setEditContent}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={saveTemplate}
                  disabled={saving}
                  className="bg-blue-600"
                  data-testid="btn-save-template"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Sauvegarder le contrat
                </Button>
                {editContent && (
                  <Button
                    variant="outline"
                    onClick={() => setShowPreview(true)}
                    data-testid="btn-preview-html"
                  >
                    <Eye className="w-4 h-4 mr-2" /> Aperçu HTML
                  </Button>
                )}
                {editContent && (
                  <Button
                    variant="outline"
                    onClick={handlePreviewPdf}
                    disabled={previewingPdf}
                    data-testid="btn-preview-pdf"
                  >
                    {previewingPdf ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    Aperçu PDF (Julie Tremblay)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== SIGNATURES — TOUS LES EMPLOYÉS (v13.56) ====== */}
        <TabsContent value="signatures" className="mt-4">
          <SectionSignaturesGlobal
            onViewHistory={(emp) => viewHistory({ employee_id: emp.employee_id, employee_nom: emp.nom_complet })}
            onViewContract={async (emp) => {
              if (!emp.signature_pdf_id || !emp.signature_id) return;
              try {
                const res = await axios.get(
                  `${API_URL}/api/contracts/signature/${emp.signature_id}/pdf`,
                  { responseType: "blob" }
                );
                const url = window.URL.createObjectURL(res.data);
                window.open(url, "_blank", "noopener");
                setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
              } catch (e) {
                toast.error("Erreur ouverture PDF");
              }
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Preview Modal (aperçu HTML rendu à l'écran) */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editTitre}</DialogTitle></DialogHeader>
          <div className="mt-4 p-6 bg-white border rounded-lg prose prose-sm max-w-none" data-testid="preview-html-content">
            <div dangerouslySetInnerHTML={{ __html: editContent }} />
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 Modal Configuration Contrat Personnalisé */}
      <Dialog open={showConfigContract} onOpenChange={setShowConfigContract}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-blue-600" />
              Configurer le contrat — {configEmployee?.employee_nom}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Remplissez les informations personnalisées pour ce contrat. Ces valeurs seront insérées automatiquement dans le contrat que l&apos;employé signera.
            </p>

            <div className="space-y-3">
              <div>
                <Label htmlFor="salaire_horaire" className="flex items-center gap-2">
                  💰 Salaire horaire <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="salaire_horaire"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="25.00"
                    value={customVars.salaire_horaire || ""}
                    onChange={(e) => setCustomVars({...customVars, salaire_horaire: e.target.value})}
                    className="flex-1"
                  />
                  <span className="text-sm text-slate-600">$ /heure</span>
                </div>
                {customVars.salaire_horaire && (
                  <p className="text-xs text-slate-500 mt-1">
                    Salaire annuel estimé: {(parseFloat(customVars.salaire_horaire) * 2080).toLocaleString('fr-CA', {minimumFractionDigits: 2})}$ (basé sur 40h/semaine)
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="date_embauche">📅 Date d&apos;embauche</Label>
                <Input
                  id="date_embauche"
                  type="date"
                  value={customVars.date_embauche || ""}
                  onChange={(e) => setCustomVars({...customVars, date_embauche: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-900 mb-1">ℹ️ Variables automatiques</p>
                <p className="text-blue-700 text-xs">
                  Le nom, prénom, titre de poste et date de signature seront automatiquement remplis lors de la signature.
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowConfigContract(false)} disabled={savingConfig} className="flex-1">
                Annuler
              </Button>
              <Button onClick={saveContractConfig} disabled={savingConfig} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Préparer pour signature
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Historique des signatures — {historyEmployee?.employee_nom}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {contractHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-6">Aucune signature enregistrée</p>
            ) : (
              contractHistory.map((sig, idx) => (
                <div key={sig.id || idx} className="p-4 border rounded-lg bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{sig.template_titre || 'Contrat de travail'}</p>
                      <p className="text-sm text-slate-500">Version {sig.template_version} — Signé par: {sig.signature_nom}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-600">
                        {new Date(sig.date_signature).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                      <Badge className="bg-green-100 text-green-700 text-xs">Signé</Badge>
                    </div>
                  </div>
                  {sig.signature_url && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-400 mb-1">Signature:</p>
                      <img src={sig.signature_url} alt="Signature" className="h-16 border rounded bg-white" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 🚀 [v13.56] Upload .docx dialog */}
      <UploadContractDialog
        open={showUpload}
        onClose={() => setShowUpload(false)}
        typeTemplate={activeType}
        defaultTitre={editTitre || TYPES_CONTRAT.find(t => t.key === activeType)?.defaultTitre}
        onUploaded={handleUploaded}
      />
    </div>
  );
};
